import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Collapse, Popover, Switch, Tag, Tooltip, Tree, message } from 'antd'
import type { DataNode } from 'antd/es/tree'
import {
  CloudServerOutlined,
  FileTextOutlined,
  FolderOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { jingqueAdminApi } from '@/api/jingqueAdminApi'
import { useAgentHarness } from '@/components/admin/harness/AgentHarnessContext'

/**
 * doc 122:VFS 文件树侧栏(popup 左栏)。
 * - antd Tree 懒加载:根 = fsBrowse ls("/"),目录展开时逐层 ls
 * - 点击文件 → 打开 Peek 预览浮卡(由本组件渲染于父级 relative 容器)
 * - 底部 [挂载点] 管理 Popover(启停 → agent Redis 全局)+ [刷新]
 * - run_as 用 h.runAsUserId(查询视角);context_id 用当前 tab 会话(scratch 作用域)
 */

interface MountItem {
  prefix: string
  kind: 'http' | 'local'
  target: string
  fs_path: string
  enabled: boolean
}

/** doc 125-Fix4:挂载缓存快照类型 */
interface CacheInfo {
  global: { disabled: string[]; age_seconds: number | null; ttl_seconds: number }
  templates: Array<{
    template_id: string
    restricted: boolean
    allowed: string[] | null
    disabled: string[]
    age_seconds: number
    ttl_seconds: number
  }>
}

interface FsEntry {
  name: string
  type: string
  summary?: string
}

const joinPath = (parent: string, name: string) =>
  parent === '/' ? `/${name}` : `${parent}/${name}`

/** 文件图标按扩展名着色,与 Peek 卡头部 ext Tag 呼应 */
const fileIcon = (name: string) => {
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : ''
  const color =
    ext === 'json' ? 'text-sky-600' : ext === 'md' ? 'text-violet-500' : 'text-muted-foreground'
  return <FileTextOutlined style={{ fontSize: 12 }} className={color} />
}

const entryToNode = (parentPath: string, e: FsEntry): DataNode => {
  const full = joinPath(parentPath, e.name)
  const isDir = e.type === 'dir'
  return {
    key: full,
    isLeaf: !isDir,
    icon: isDir ? (
      <FolderOutlined style={{ fontSize: 12 }} className="text-amber-500" />
    ) : (
      fileIcon(e.name)
    ),
    title: (
      // 悬停:全路径 + summary(名称截断时在这里看全名)
      <Tooltip
        title={
          <div>
            <div className="font-medium">{full}</div>
            {e.summary && <div className="max-w-[280px] text-[10px] opacity-70">{e.summary}</div>}
          </div>
        }
        placement="right"
        mouseEnterDelay={0.4}
      >
        <span className="text-xs">{e.name}</span>
      </Tooltip>
    ),
  }
}

/** 挂载点管理 Popover 内容:列表 + Switch 启停。
 *  doc 125-Fix3:白名单感知——开关展示「全局开关 ∩ 模板白名单」的有效可见态;
 *  白名单外的挂载显示未勾 + disabled + 「白名单外」标签(开关对它无效故禁用,免困惑) */
const MountsPanel: FC<{ onChanged: () => void }> = ({ onChanged }) => {
  const h = useAgentHarness()
  const [items, setItems] = useState<MountItem[]>([])
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState('')
  // doc 125-Fix4:缓存诊断 + 清除
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null)
  const [clearingTpl, setClearingTpl] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await jingqueAdminApi.agentHarness.fsMounts()
      setItems(r.mounts || [])
    } catch (e: any) {
      message.error('加载挂载点失败: ' + (e?.message || String(e)))
    } finally {
      setLoading(false)
    }
  }, [])

  const loadCache = useCallback(async () => {
    try {
      const r = await jingqueAdminApi.agentHarness.fsMountsCache()
      setCacheInfo(r as CacheInfo)
    } catch {
      // 静默:缓存读失败不影响主面板
    }
  }, [])

  useEffect(() => {
    load()
    loadCache()
  }, [load, loadCache])

  const toggle = async (m: MountItem, enabled: boolean) => {
    setToggling(m.prefix)
    try {
      await jingqueAdminApi.agentHarness.fsSetMount(m.prefix, enabled)
      setItems(prev => prev.map(x => (x.prefix === m.prefix ? { ...x, enabled } : x)))
      message.success(`${m.prefix} 已${enabled ? '启用' : '禁用'}`)
      onChanged()
    } catch (e: any) {
      message.error(e?.message || String(e))
    } finally {
      setToggling('')
    }
  }

  // doc 125-Fix4:清模板缓存 — 单条/全量;成功后重拉缓存 + 触发文件树重载(下次 fs_browse 即按最新白名单)
  const clearCache = async (templateId?: string) => {
    setClearingTpl(templateId || 'ALL')
    try {
      const r = await jingqueAdminApi.agentHarness.fsMountsCacheClear(templateId)
      message.success(`已清除 ${r.cleared} 条模板缓存,文件树即时重载`, 2000)
      await loadCache()
      onChanged()
    } catch (e: any) {
      message.error(e?.message || String(e))
    } finally {
      setClearingTpl('')
    }
  }

  // doc 125:undefined/空数组 = 不限制(与 agent 两态语义一致);h.tpl 未加载时亦视为不限制
  const whitelist = h.tpl?.fsAllowedMounts
  const restricted = Array.isArray(whitelist) && whitelist.length > 0

  return (
    <div className="flex w-[320px] flex-col gap-1">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium">挂载点</span>
        <Button type="text" size="small" icon={<ReloadOutlined style={{ fontSize: 11 }} />} onClick={load} />
      </div>
      <div className="-mt-1 mb-1 text-[10px] text-muted-foreground">
        当前模板 {h.tpl?.templateId || '-'} · {restricted ? `白名单 ${whitelist.length} 项` : '不限制'}
      </div>
      {loading && <div className="py-3 text-center text-xs text-muted-foreground">加载中…</div>}
      {!loading &&
        items.map(m => {
          const inWhitelist = !restricted || whitelist.includes(m.prefix)
          const effectiveOn = inWhitelist && m.enabled
          return (
            <div key={m.prefix} className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted/50">
              <CloudServerOutlined
                style={{ fontSize: 12 }}
                className={m.kind === 'local' ? 'text-emerald-600' : 'text-sky-600'}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="truncate font-mono text-xs">{m.prefix}</span>
                  {!inWhitelist && (
                    <Tooltip title="当前模板白名单未包含此挂载,LLM 与文件树均不可见。请在「对话模板」编辑页的白名单区配置。">
                      <Tag color="default" className="!m-0 !text-[10px] !leading-4">白名单外</Tag>
                    </Tooltip>
                  )}
                </div>
                <div className="truncate text-[10px] text-muted-foreground" title={m.target}>
                  {m.kind} · {m.target}
                </div>
              </div>
              <Switch
                size="small"
                checked={effectiveOn}
                disabled={!inWhitelist}
                loading={toggling === m.prefix}
                onChange={v => toggle(m, v)}
              />
            </div>
          )
        })}
      <div className="mt-1 border-t pt-1 text-[10px] text-muted-foreground">
        可见性 = 全局开关 ∩ 模板白名单;白名单在「对话模板」编辑页配置
      </div>
      {/* doc 125-Fix4:挂载缓存诊断 + 手动清除 */}
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: 'cache',
            label: <span className="text-xs">缓存</span>,
            children: (
              <div className="flex flex-col gap-1">
                {cacheInfo === null ? (
                  <div className="py-1 text-[10px] text-muted-foreground">加载中…</div>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-[10px]">
                      <span>
                        全局禁用 {cacheInfo.global.disabled.length} 项 · 缓存{' '}
                        {cacheInfo.global.age_seconds !== null ? `${cacheInfo.global.age_seconds}s` : '—'}
                        /{cacheInfo.global.ttl_seconds}s
                      </span>
                      <Tooltip title="5s TTL 自动过期,无需手动清除">
                        <span className="text-[10px] text-muted-foreground">只读</span>
                      </Tooltip>
                    </div>
                    {cacheInfo.templates.length === 0 ? (
                      <div className="py-1 text-[10px] text-muted-foreground">
                        暂无模板缓存(进程启动后尚未查询过)
                      </div>
                    ) : (
                      cacheInfo.templates.map(t => {
                        const isCurrent = t.template_id === h.tpl?.templateId
                        return (
                          <div
                            key={t.template_id}
                            className="flex items-center justify-between rounded-md px-1 py-0.5 hover:bg-muted/30"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1">
                                <span className={`truncate font-mono text-[11px] ${isCurrent ? 'font-semibold text-blue-600' : ''}`}>
                                  {t.template_id}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {t.restricted
                                    ? `白名单 ${t.allowed?.length ?? 0} 项`
                                    : '不限制'}
                                </span>
                              </div>
                              <div className="text-[10px] text-muted-foreground">
                                {t.age_seconds}s/{t.ttl_seconds}s
                              </div>
                            </div>
                            <Tooltip title="清除该模板缓存,下次 fs 调用即时按最新白名单重算">
                              <Button
                                type="text"
                                size="small"
                                icon={<ReloadOutlined style={{ fontSize: 11 }} />}
                                loading={clearingTpl === t.template_id}
                                onClick={() => clearCache(t.template_id)}
                              />
                            </Tooltip>
                          </div>
                        )
                      })
                    )}
                    {cacheInfo.templates.length > 0 && (
                      <div className="mt-1 flex items-center justify-between border-t pt-1">
                        <span className="text-[10px] text-muted-foreground">
                          缓存仅在 agent 文字路径进程,清除后文件树立即按新白名单重载
                        </span>
                        <Button
                          type="link"
                          size="small"
                          loading={clearingTpl === 'ALL'}
                          onClick={() => clearCache()}
                        >
                          清空全部
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  )
}

export const FsTreeSidebar: FC<{ onOpenFile: (path: string) => void }> = ({ onOpenFile }) => {
  const h = useAgentHarness()
  const [treeData, setTreeData] = useState<DataNode[]>([])
  const [loadingRoot, setLoadingRoot] = useState(true)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const [refreshTick, setRefreshTick] = useState(0)

  const browse = useCallback(
    async (path: string): Promise<FsEntry[]> => {
      const r = await jingqueAdminApi.agentHarness.fsBrowse({
        action: 'ls',
        path,
        context_id: h.contextId || undefined,
        run_as: h.runAsUserId || undefined,
        // doc 125-Fix2:文件树跟随当前 tab 模板白名单(挂载启停面板仍展示全集 + 全局开关)
        template_id: h.templateId || undefined,
      })
      if (r.error) {
        message.warning(r.error.error || '浏览失败')
        return []
      }
      return r.envelope?.data?.entries || []
    },
    [h.contextId, h.runAsUserId, h.templateId],
  )

  // 根加载 + 挂载变更/手动刷新后重载
  useEffect(() => {
    let cancelled = false
    setLoadingRoot(true)
    browse('/')
      .then(entries => {
        if (!cancelled) setTreeData(entries.map(e => entryToNode('/', e)))
      })
      .finally(() => !cancelled && setLoadingRoot(false))
    return () => {
      cancelled = true
    }
  }, [browse, refreshTick])

  // 懒加载子目录
  const onLoadData = useCallback(
    async (node: DataNode): Promise<void> => {
      const path = String(node.key)
      const entries = await browse(path)
      setTreeData(prev => updateChildren(prev, path, entries.map(e => entryToNode(path, e))))
    },
    [browse],
  )

  const refresh = () => {
    // 刷新会重置子节点数据,展开态一并清空(否则展开目录显示空)
    setExpandedKeys([])
    setRefreshTick(t => t + 1)
  }

  // 点名称展开/收起目录(switcher 箭头已隐藏);展开未加载目录时手动触发 loadData
  const onNodeClick = (node: DataNode) => {
    const key = String(node.key)
    if (node.isLeaf) {
      onOpenFile(key)
      return
    }
    if (expandedKeys.includes(key)) {
      setExpandedKeys(expandedKeys.filter(k => k !== key))
    } else {
      setExpandedKeys([...expandedKeys, key])
      if (node.children === undefined) onLoadData(node)
    }
  }

  const tree = useMemo(
    () => (
      // doc 122 样式修正:紧凑布局全部走 index.css 的 .fs-tree 作用域样式
      // (antd v6 token 路线在 cssinjs 下不稳,改直接 CSS 覆盖)
      <Tree
        showIcon
        blockNode
        selectable={false}
        treeData={treeData}
        loadData={onLoadData}
        expandedKeys={expandedKeys}
        onExpand={keys => setExpandedKeys(keys.map(String))}
        onClick={(_e, node) => onNodeClick(node)}
        className="fs-tree text-xs [&_.ant-tree-treenode]:w-full [&_.ant-tree-node-content-wrapper]:cursor-pointer"
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [treeData, onLoadData, expandedKeys, onOpenFile],
  )

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* 树主体(px-2 补回侧栏内边距) */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-1.5">
        {loadingRoot && <div className="py-3 text-center text-xs text-muted-foreground">加载中…</div>}
        {!loadingRoot && treeData.length === 0 && (
          <div className="py-3 text-center text-xs text-muted-foreground">文件系统为空</div>
        )}
        {!loadingRoot && treeData.length > 0 && tree}
      </div>

      {/* 底部操作行:挂载点管理 + 刷新 */}
      <div className="flex h-8 shrink-0 items-center gap-1 border-t px-1">
        <Popover
          trigger="click"
          placement="topLeft"
          content={<MountsPanel onChanged={refresh} />}
        >
          <Button type="text" size="small" icon={<CloudServerOutlined style={{ fontSize: 12 }} />}>
            <span className="text-xs">挂载点</span>
          </Button>
        </Popover>
        <span className="flex-1" />
        <Tooltip title="刷新文件树">
          <Button type="text" size="small" icon={<ReloadOutlined style={{ fontSize: 11 }} />} onClick={refresh} />
        </Tooltip>
      </div>
    </div>
  )
}

function updateChildren(nodes: DataNode[], path: string, children: DataNode[]): DataNode[] {
  return nodes.map(n => {
    if (n.key === path) return { ...n, children }
    if (n.children) return { ...n, children: updateChildren(n.children, path, children) }
    return n
  })
}
